/**
 * AdminTryoutTokensView – Manage Cold-Email Tryout campaign tokens.
 * Shown as a tab in AdminDashboard.
 *
 * Features:
 * - Create tokens with campaign name, source, max_uses
 * - Toggle active/inactive (is_active)
 * - Copy tryout URL or {{tryout_link}} template variable
 * - View lead capture stats per token
 * - Delete tokens
 */

import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle,
  ChevronDown,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  Pause,
  Play,
  Plus,
  Trash2,
  Upload,
  Users,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { TryoutListUploadModal } from './TryoutListUploadModal';
import { AdminTryoutPresetsView } from './AdminTryoutPresetsView';
import { AdminTryoutCapturesView } from './AdminTryoutCapturesView';

// ============================================================================
// Types
// ============================================================================

interface TryoutToken {
  id: string;
  token: string;
  name: string;
  usage_count: number;
  max_uses: number;
  campaign_name: string | null;
  source: string;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  lead_count?: number;
}

interface TryoutLeadList {
  id: string;
  name: string;
  campaign_tag: string | null;
  source: string;
  created_at: string;
  total_leads: number;
  active_leads: number;
  conversion_count: number;
}

interface AdminTryoutTokensViewProps {
  onOpenList?: (listId: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

const getTryoutUrl = (token: string) =>
  `${window.location.origin}/tryout/${encodeURIComponent(token)}`;

const getTemplateVariable = (token: string) =>
  `{{tryout_link:${token}}}`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manuell',
  cold_email: 'Cold E-Mail',
  landing_page: 'Landing Page',
};

// ============================================================================
// Component
// ============================================================================

export const AdminTryoutTokensView: React.FC<AdminTryoutTokensViewProps> = ({ onOpenList }) => {
  const [tokens, setTokens] = useState<TryoutToken[]>([]);
  const [lists, setLists] = useState<TryoutLeadList[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showManualSection, setShowManualSection] = useState(false);
  const [subTab, setSubTab] = useState<'links' | 'presets' | 'captures'>('links');
  const [isLoading, setIsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedType, setCopiedType] = useState<'url' | 'template' | null>(null);

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCampaign, setNewCampaign] = useState('');
  const [newSource, setNewSource] = useState('cold_email');
  const [newMaxUses, setNewMaxUses] = useState(3);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Toggle active
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // ============================================================================
  // Fetch tokens with lead counts
  // ============================================================================

  const fetchTokens = useCallback(async () => {
    setIsLoading(true);

    const [tokensRes, listsRes] = await Promise.all([
      supabase
        .from('tryout_tokens')
        .select('*, tryout_leads(count)')
        .is('list_id', null)
        .order('created_at', { ascending: false }),
      supabase.rpc('admin_get_tryout_lead_lists'),
    ]);

    if (!tokensRes.error && tokensRes.data) {
      const mapped = tokensRes.data.map((t: TryoutToken & { tryout_leads?: { count: number }[] }) => ({
        ...t,
        lead_count: t.tryout_leads?.[0]?.count ?? 0,
      })) as TryoutToken[];
      setTokens(mapped);
    }

    if (!listsRes.error && listsRes.data) {
      setLists(listsRes.data as TryoutLeadList[]);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => { fetchTokens(); }, [fetchTokens]);

  // ============================================================================
  // Create token
  // ============================================================================

  const handleCreate = async () => {
    setIsCreating(true);
    setCreateError(null);

    const name = newName.trim() || `Tryout ${new Date().toLocaleDateString('de-DE')}`;

    const { error } = await supabase
      .from('tryout_tokens')
      .insert({
        name,
        campaign_name: newCampaign.trim() || null,
        source: newSource,
        max_uses: newMaxUses,
      });

    if (error) {
      setCreateError(error.message);
      setIsCreating(false);
      return;
    }

    setShowCreate(false);
    setNewName('');
    setNewCampaign('');
    setNewSource('cold_email');
    setNewMaxUses(3);
    setIsCreating(false);
    fetchTokens();
  };

  // ============================================================================
  // Toggle active
  // ============================================================================

  const handleToggleActive = async (token: TryoutToken) => {
    setTogglingId(token.id);
    await supabase
      .from('tryout_tokens')
      .update({ is_active: !token.is_active })
      .eq('id', token.id);
    setTogglingId(null);
    fetchTokens();
  };

  // ============================================================================
  // Copy
  // ============================================================================

  const handleCopy = async (token: TryoutToken, type: 'url' | 'template') => {
    const text = type === 'url'
      ? getTryoutUrl(token.token)
      : getTemplateVariable(token.token);
    await navigator.clipboard.writeText(text);
    setCopiedId(token.id);
    setCopiedType(type);
    setTimeout(() => { setCopiedId(null); setCopiedType(null); }, 2000);
  };

  // ============================================================================
  // Delete token
  // ============================================================================

  const handleDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    await supabase.from('tryout_tokens').delete().eq('id', deletingId);
    setDeletingId(null);
    setIsDeleting(false);
    fetchTokens();
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="space-y-4">
      {/* Sub-tabs: Links | Beispielbilder (nested here to keep the top admin bar short) */}
      <div className="flex items-center gap-1 border-b border-border">
        {([['links', 'Links'], ['presets', 'Beispielbilder'], ['captures', 'KI-Qualität']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setSubTab(key)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              subTab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {subTab === 'presets' && <AdminTryoutPresetsView />}

      {subTab === 'captures' && <AdminTryoutCapturesView />}

      {subTab === 'links' && (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Tryout-Kampagnen</h2>
          <p className="text-sm text-muted-foreground">
            Personalisierte Staging-Tryout-Links für Cold-Email-Kampagnen mit Lead-Capture.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowUploadModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Lead-Liste hochladen
          </Button>
          <Button onClick={() => { setShowCreate(true); setNewName(''); setNewCampaign(''); setCreateError(null); }}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Token
          </Button>
        </div>
      </div>

      {/* Lead-Listen */}
      <div className="mb-2">
        <h3 className="text-base font-semibold mb-3">Lead-Listen</h3>
        {lists.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              Noch keine Listen. Lade eine CSV hoch um zu starten.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {lists.map((l) => (
              <Card
                key={l.id}
                className="cursor-pointer hover:border-primary transition"
                onClick={() => onOpenList?.(l.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <div className="font-medium">{l.name}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                      {SOURCE_LABELS[l.source] ?? l.source}
                    </span>
                  </div>
                  {l.campaign_tag && (
                    <div className="text-xs text-muted-foreground mb-2">{l.campaign_tag}</div>
                  )}
                  <div className="text-sm text-muted-foreground">
                    {l.total_leads} Leads · {l.active_leads} aktiv · {l.conversion_count} Conversions · {formatDate(l.created_at)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Manuelle Tokens (collapsible) */}
      <div>
        <button
          onClick={() => setShowManualSection((v) => !v)}
          className="flex items-center gap-2 text-base font-semibold hover:text-primary"
        >
          <ChevronDown className={`h-4 w-4 transition ${showManualSection ? 'rotate-0' : '-rotate-90'}`} />
          Manuelle Tokens ({tokens.length})
        </button>
      </div>

      {/* Token list */}
      {showManualSection && (isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : tokens.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground text-sm">
              Noch keine Tryout-Tokens erstellt.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tokens.map((t) => {
            const url = getTryoutUrl(t.token);
            const usagePct = (t.usage_count / t.max_uses) * 100;
            const isFull = t.usage_count >= t.max_uses;
            const isToggling = togglingId === t.id;

            return (
              <Card key={t.id} className={cn(!t.is_active && 'opacity-60')}>
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3">
                    {/* Top row: name + badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{t.name}</span>
                      {t.campaign_name && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-500/10 text-blue-600 rounded">
                          {t.campaign_name}
                        </span>
                      )}
                      <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded">
                        {SOURCE_LABELS[t.source] || t.source}
                      </span>
                      {!t.is_active && (
                        <span className="text-xs px-1.5 py-0.5 bg-amber-500/10 text-amber-600 rounded">
                          Pausiert
                        </span>
                      )}
                      {isFull && (
                        <span className="text-xs px-1.5 py-0.5 bg-destructive/10 text-destructive rounded">
                          Aufgebraucht
                        </span>
                      )}
                    </div>

                    {/* URL */}
                    <p className="text-xs text-muted-foreground font-mono truncate">
                      {url}
                    </p>

                    {/* Stats row */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {/* Usage bar */}
                      <div className="flex items-center gap-2 flex-1">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${isFull ? 'bg-destructive' : 'bg-primary'}`}
                            style={{ width: `${usagePct}%` }}
                          />
                        </div>
                        <span className="whitespace-nowrap">
                          {t.usage_count}/{t.max_uses}
                        </span>
                      </div>

                      {/* Lead count */}
                      <div className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        <span>{t.lead_count ?? 0} Leads</span>
                      </div>

                      {/* Created date */}
                      <span>{formatDate(t.created_at)}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Copy URL */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(t, 'url')}
                        className="gap-1.5"
                      >
                        {copiedId === t.id && copiedType === 'url' ? (
                          <><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> Kopiert</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5" /> URL</>
                        )}
                      </Button>

                      {/* Copy template variable */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(t, 'template')}
                        className="gap-1.5"
                      >
                        {copiedId === t.id && copiedType === 'template' ? (
                          <><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> Kopiert</>
                        ) : (
                          <><Mail className="w-3.5 h-3.5" /> Template</>
                        )}
                      </Button>

                      {/* Open link */}
                      <Button variant="outline" size="icon" asChild title="Link öffnen">
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </Button>

                      {/* Toggle active */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleToggleActive(t)}
                        disabled={isToggling}
                        title={t.is_active ? 'Pausieren' : 'Aktivieren'}
                      >
                        {isToggling ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : t.is_active ? (
                          <Pause className="w-3.5 h-3.5" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                      </Button>

                      {/* Delete */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setDeletingId(t.id)}
                        title="Token löschen"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ))}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Neuen Tryout-Token erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Name */}
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={`Tryout ${new Date().toLocaleDateString('de-DE')}`}
                autoFocus
              />
            </div>

            {/* Campaign Name */}
            <div className="space-y-2">
              <Label>Kampagnen-Name (optional)</Label>
              <Input
                value={newCampaign}
                onChange={(e) => setNewCampaign(e.target.value)}
                placeholder="z.B. Kaltakquise März 2026"
              />
            </div>

            {/* Source */}
            <div className="space-y-2">
              <Label>Quelle</Label>
              <div className="relative">
                <select
                  value={newSource}
                  onChange={(e) => setNewSource(e.target.value)}
                  className="w-full appearance-none bg-background border border-border rounded-lg px-3 py-2 text-sm pr-8 focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="cold_email">Cold E-Mail</option>
                  <option value="landing_page">Landing Page</option>
                  <option value="manual">Manuell</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Max Uses */}
            <div className="space-y-2">
              <Label>Max. Versuche pro Token</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={newMaxUses}
                onChange={(e) => setNewMaxUses(parseInt(e.target.value) || 3)}
              />
              <p className="text-xs text-muted-foreground">
                Jeder Versuch generiert ein Staging- und ein Leerraum-Bild.
              </p>
            </div>

            {createError && (
              <p className="text-sm text-destructive">{createError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={isCreating}>
              Abbrechen
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Token löschen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Der Token und alle zugehörigen Leads werden permanent gelöscht.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)} disabled={isDeleting}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload list modal */}
      <TryoutListUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onCreated={(listId) => {
          setShowUploadModal(false);
          onOpenList?.(listId);
        }}
      />
      </>
      )}
    </div>
  );
};
